export const SUPPORTED_LOCALES = Object.freeze(["pt-BR", "en-US", "es-MX"]);
export const DEFAULT_LOCALE = "pt-BR";
export const LOCALE_STORAGE_KEY = "cajupe.locale";

export const LANGUAGE_OPTIONS = Object.freeze([
  { item: "pt-BR", description: "Português (Brasil)" },
  { item: "en-US", description: "English (United States)" },
  { item: "es-MX", description: "Español (México)" },
]);

const LOCALE_BY_LANGUAGE = Object.freeze({
  pt: "pt-BR",
  en: "en-US",
  es: "es-MX",
});

export const normalizeLocale = (locale) => {
  const normalizedLocale = String(locale || "")
    .trim()
    .replace(/_/g, "-");
  const language = normalizedLocale.split("-")[0].toLowerCase();

  return LOCALE_BY_LANGUAGE[language] || null;
};

const getBrowserStorage = () => {
  if (typeof window === "undefined") return null;

  try {
    return window.localStorage;
  } catch (error) {
    return null;
  }
};

const getBrowserNavigator = () => {
  if (typeof navigator === "undefined") return null;
  return navigator;
};

export const getDeviceLocale = (browserNavigator = getBrowserNavigator()) => {
  if (!browserNavigator) return null;

  const candidates = [
    ...(Array.isArray(browserNavigator.languages)
      ? browserNavigator.languages
      : []),
    browserNavigator.language,
  ];

  for (const candidate of candidates) {
    const locale = normalizeLocale(candidate);
    if (locale) return locale;
  }

  return null;
};

export const getSavedLocale = (storage = getBrowserStorage()) => {
  if (!storage) return null;

  try {
    const savedLocale = storage.getItem(LOCALE_STORAGE_KEY);
    const normalizedLocale = normalizeLocale(savedLocale);

    if (normalizedLocale && savedLocale !== normalizedLocale) {
      storage.setItem(LOCALE_STORAGE_KEY, normalizedLocale);
    }

    return normalizedLocale;
  } catch (error) {
    return null;
  }
};

export const resolveInitialLocale = ({
  storage = getBrowserStorage(),
  browserNavigator = getBrowserNavigator(),
} = {}) =>
  getSavedLocale(storage) ||
  getDeviceLocale(browserNavigator) ||
  DEFAULT_LOCALE;

export const saveLocale = (locale, storage = getBrowserStorage()) => {
  const normalizedLocale = normalizeLocale(locale) || DEFAULT_LOCALE;

  if (storage) {
    try {
      storage.setItem(LOCALE_STORAGE_KEY, normalizedLocale);
    } catch (error) {
      // Storage may be unavailable in private or restricted browser contexts.
    }
  }

  return normalizedLocale;
};

export const syncDocumentLocale = (locale) => {
  const normalizedLocale = normalizeLocale(locale) || DEFAULT_LOCALE;

  if (typeof document !== "undefined") {
    document.documentElement.lang = normalizedLocale;
  }

  return normalizedLocale;
};
